import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  View,
} from 'react-native';
import { styled, SxProp } from 'dripsy';
import { observer, useLocalObservable } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import {
  CompositeNavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';

import {
  BaseText,
  CommentInputModeEnum,
  CommentList,
  EmptyResultView,
  FlatListRefType,
  Separator,
  ViewerTextInputRefType,
} from '@components';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import {
  HubNavigatorNavigationPropType,
  HubNavigatorRoutePropType,
  MainNavigatorNavigationPropType,
  MainNavigatorRoutePropType,
  screens,
} from '@navigation';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { Comment } from '@models/Comments/CommentModel';
import { CommentInputFooter } from './components/CommentInputFooter';
import { globalStyles } from '@styles';
import { User } from '@models/Viewer/UserModel';

interface IContainerStyleProps {
  insets: EdgeInsets;
}

const Container = styled(View)((props: IContainerStyleProps) => ({
  flex: 1,
  bg: '$background0',
  pb: props.insets.bottom,
}));

const contentContainerStyle: SxProp = {
  flexGrow: 1,
  px: '$6',
};

const CommentsCount = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

export interface ICommentScreenStore {
  comment: Comment | null;
  mode: CommentInputModeEnum;
  setComment(comment: Comment, mode: CommentInputModeEnum): void;
  clearComment(): void;
  get isUpdateMode(): boolean;
}

type RoutePropType =
  | MainNavigatorRoutePropType<'Comment'>
  | HubNavigatorRoutePropType<'Comment'>;

type NavigationPropType = CompositeNavigationProp<
  MainNavigatorNavigationPropType<'Comment'>,
  HubNavigatorNavigationPropType<'Comment'>
>;

export const CommentScreen = observer(() => {
  const store = useLocalObservable<ICommentScreenStore>(() => ({
    comment: null,
    mode: CommentInputModeEnum.Create,

    setComment(value: Comment, mode: CommentInputModeEnum) {
      this.comment = value;
      this.mode = mode;
    },

    clearComment() {
      this.comment = null;
      this.mode = CommentInputModeEnum.Create;
    },

    get isUpdateMode() {
      return this.mode === CommentInputModeEnum.Update;
    },
  }));
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const route = useRoute<RoutePropType>();
  const inputRef = useRef<ViewerTextInputRefType>(null);
  const listRef = useRef<FlatListRefType>(null);
  const nav = useNavigation<NavigationPropType>();
  const { list } = route.params;

  useNavOptionsSet({ title: t('comment.title') });

  useAsyncInit(() => fetch());

  useEffect(() => {
    Keyboard.addListener('keyboardDidShow', () => {
      if (store.comment) {
        listRef.current?.scrollToItem({
          item: store.comment,
          viewPosition: 0,
        });
      }
    });
  }, []);

  const onNavigateToUser = useCallback((user: User) => {
    nav.push(screens.UserProfile, {
      id: user.id,
    });
  }, []);

  async function fetch() {
    try {
      await list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await list.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const replyToComment = useCallback((item: Comment) => {
    store.setComment(item, CommentInputModeEnum.Create);
    inputRef.current?.focus();
  }, []);
  const editComment = useCallback((item: Comment) => {
    store.setComment(item, CommentInputModeEnum.Update);
    inputRef.current?.focus();
  }, []);

  const isLoading = list.fetch.inProgress && list.isEmpty;
  const keyboardVerticalOffset = 64 + insets.top;
  const commentsCount = t('comment.comment', {
    count: list.totalCount,
  });

  return (
    <Container insets={insets}>
      <KeyboardAvoidingView
        behavior={Platform.select({ ios: 'padding' })}
        keyboardVerticalOffset={keyboardVerticalOffset}
        style={globalStyles.wrapper}
      >
        <CommentList
          listRef={listRef}
          ListHeaderComponent={() => (
            <Fragment>
              <CommentsCount>{commentsCount}</CommentsCount>
              <Separator size="$6" />
            </Fragment>
          )}
          onPressReply={replyToComment}
          onPressEdit={editComment}
          data={list.asArray}
          contentContainerSx={contentContainerStyle}
          isLoading={isLoading}
          isLoadingMore={list.fetchMore.inProgress}
          isRefreshing={list.fetch.inProgress}
          onRefresh={fetch}
          onEndReached={fetchMore}
          ListEmptyComponent={() =>
            list.fetch.isError ? <EmptyResultView /> : null
          }
          onPressUser={onNavigateToUser}
        />

        <CommentInputFooter
          list={list}
          store={store}
          inputRef={inputRef}
        />
      </KeyboardAvoidingView>
    </Container>
  );
});
